// src/components/Tutorial/MiniNavigation.js
import React from 'react';
import {
  Box,
  Paper,
  AppBar,
  Toolbar,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
  Button
} from '@mui/material';
import { 
  People, 
  EmojiEvents, 
  Star, 
  AccountCircle,
  Logout
} from '@mui/icons-material';

// Mini navigation component for tutorial - matches actual app navigation exactly
const MiniNavigation = ({ isMobile, highlightSection }) => {
  const navItems = [
    { text: 'Creators', icon: <People />, section: 'creators' },
    { text: 'Leaderboard', icon: <EmojiEvents />, section: 'leaderboard' },
    { text: 'Reviews', icon: <Star />, section: 'reviews' }
  ];

  if (isMobile) {
    // Mobile bottom navigation - matches MobileNav.js exactly
    return (
      <Box sx={{ my: 3 }}>
        <Paper elevation={3} sx={{ maxWidth: 400, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Mobile Navigation (Bottom of Screen)
            </Typography>
          </Box>
          <Paper 
            elevation={8}
            sx={{ 
              borderRadius: '16px 16px 0 0',
              overflow: 'hidden'
            }}
          >
            <BottomNavigation
              showLabels
              sx={{
                height: 64,
                '& .MuiBottomNavigationAction-root': {
                  minWidth: 'auto',
                  padding: '6px 0',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  '&.Mui-selected': {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  },
                },
              }}
            >
              {navItems.map((item) => (
                <BottomNavigationAction
                  key={item.text}
                  label={item.text}
                  icon={item.icon}
                  sx={{
                    transition: 'transform 0.2s',
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                />
              ))}
              <BottomNavigationAction
                label="Logout"
                icon={<Logout />}
                sx={{
                  transition: 'transform 0.2s',
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              />
            </BottomNavigation>
          </Paper>
        </Paper>
      </Box>
    );
  }

  // Desktop top navigation - matches Navbar.js exactly
  return (
    <Box sx={{ my: 3 }}>
      <Paper elevation={3} sx={{ maxWidth: 700, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Desktop Navigation (Top of Screen)
          </Typography>
        </Box>
        <AppBar position="static" elevation={0} color="primary">
          <Toolbar sx={{ px: 4, py: 1, justifyContent: 'space-between', minHeight: '48px !important' }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 5 }}>
              <Box
                component="img"
                src="/logo.png"
                alt="OnlyStats Logo"
                sx={{ height: 36 }}
              />
            </Box>
            
            {/* Navigation items */}
            <Box sx={{ display: 'flex', gap: 2.5 }}>
              {navItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  startIcon={item.icon}
                  size="small"
                  sx={{
                    position: 'relative',
                    px: 2.5,
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 500,
                    color: 'white',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    minWidth: 'auto',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
            
            {/* Profile button on the right end */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                color="inherit"
                startIcon={<AccountCircle />}
                size="small"
                sx={{
                  position: 'relative',
                  px: 2.5,
                  py: 1,
                  borderRadius: 1,
                  fontWeight: 500,
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  minWidth: 'auto',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                Profile
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      </Paper>
    </Box>
  );
};

export default MiniNavigation;
